/**
 * Cookie management utilities
 * Handles cookie consent, storage, and retrieval
 */

// Cookie categories
export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  FUNCTIONAL: 'functional',
  MARKETING: 'marketing'
};

// Cookie consent storage key
const CONSENT_KEY = 'cookie_consent';

// Default cookie settings
const DEFAULT_SETTINGS = {
  [COOKIE_CATEGORIES.NECESSARY]: true, // Always required
  [COOKIE_CATEGORIES.ANALYTICS]: false,
  [COOKIE_CATEGORIES.FUNCTIONAL]: false,
  [COOKIE_CATEGORIES.MARKETING]: false
};

/**
 * Get cookie consent settings from localStorage
 * @returns {Object} Cookie consent settings
 */
export const getCookieConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Save cookie consent settings to localStorage
 * @param {Object} settings - Cookie consent settings
 */
export const saveCookieConsent = (settings) => {
  try {
    const consentData = {
      ...settings,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
  } catch (error) {
    console.error('Error saving cookie consent:', error);
  }
};

/**
 * Check if user has given consent
 * @returns {boolean} True if consent has been given
 */
export const hasConsent = () => {
  const consent = getCookieConsent();
  return consent !== null;
};

/**
 * Get default cookie settings
 * @returns {Object} Default cookie settings
 */
export const getDefaultSettings = () => {
  return { ...DEFAULT_SETTINGS };
};

/**
 * Set a cookie with consent checking
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options
 * @param {string} category - Cookie category
 */
export const setCookie = (name, value, options = {}, category = COOKIE_CATEGORIES.NECESSARY) => {
  const consent = getCookieConsent();
  
  // Allow necessary cookies or if user has consented to the category
  if (category === COOKIE_CATEGORIES.NECESSARY || (consent && consent[category])) {
    const cookieString = `${name}=${value}; path=/; ${Object.entries(options)
      .map(([key, val]) => `${key}=${val}`)
      .join('; ')}`;
    document.cookie = cookieString;
  }
};

/**
 * Get a cookie value
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null
 */
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path
 */
export const deleteCookie = (name, path = '/') => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
};

/**
 * Clear all non-necessary cookies
 */
export const clearNonNecessaryCookies = () => {
  const consent = getCookieConsent();
  if (!consent) return;

  // List of cookies to potentially clear (add your actual cookie names here)
  const cookiesToCheck = [
    '_ga', '_ga_*', '_gid', '_gat', '_fbp', '_fbc', // Analytics
    'session_id', 'user_preferences', // Functional
    'marketing_campaign', 'ad_targeting' // Marketing
  ];

  // Clear cookies that user hasn't consented to
  Object.keys(COOKIE_CATEGORIES).forEach(category => {
    if (category !== COOKIE_CATEGORIES.NECESSARY && !consent[COOKIE_CATEGORIES[category]]) {
      cookiesToCheck.forEach(cookieName => {
        deleteCookie(cookieName);
      });
    }
  });
};

/**
 * Initialize Google Analytics based on consent
 */
export const initializeAnalytics = () => {
  const consent = getCookieConsent();
  if (consent && consent[COOKIE_CATEGORIES.ANALYTICS]) {
    // Initialize GA4 if consent is given
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted'
      });
    }
  } else {
    // Deny analytics if no consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      });
    }
  }
};

/**
 * Cookie information for the settings modal
 */
export const COOKIE_INFO = {
  [COOKIE_CATEGORIES.NECESSARY]: {
    name: 'Necessary Cookies',
    description: 'These cookies are essential for the website to function properly. They cannot be disabled.',
    examples: ['Authentication', 'Security', 'Basic functionality'],
    required: true
  },
  [COOKIE_CATEGORIES.ANALYTICS]: {
    name: 'Analytics Cookies',
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    examples: ['Google Analytics', 'Page views', 'User behavior'],
    required: false
  },
  [COOKIE_CATEGORIES.FUNCTIONAL]: {
    name: 'Functional Cookies',
    description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences.',
    examples: ['Language settings', 'User preferences', 'Customization'],
    required: false
  },
  [COOKIE_CATEGORIES.MARKETING]: {
    name: 'Marketing Cookies',
    description: 'These cookies are used to track visitors across websites to display relevant and engaging advertisements.',
    examples: ['Ad targeting', 'Campaign tracking', 'Social media integration'],
    required: false
  }
};
